import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Button,
  CircularProgress,
  colors,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGrid,
  DataGridProps,
  gridClasses,
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnVisibilityModel,
  GridFilterListIcon,
  GridFilterModel,
  GridRowsProp,
  GridSearchIcon,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  useGridApiRef,
} from '@mui/x-data-grid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  formatAppointmentDateTime,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPhoneNumber,
  safeString,
} from './dataFormatter';
import { getGridSettings, updateGridSettings } from 'store/slices/adminSlice';
import { useAppDispatch } from 'store/hooks';

dayjs.extend(utc);

// Define color constants
const COLORS = {
  LightYellow: '#FFF8E1',
  Black: '#000000',
  White: '#FFFFFF',
  LightBlue: '#E3F2FD',
  LightGray: '#F5F5F5',
};

const btnSx = {
  backgroundColor: 'transparent',
  color: '#3467FF',
  minWidth: '100px',
  boxShadow: 'none',
  pl: 0,
  '&:hover': { backgroundColor: 'transparent', boxShadow: 'none' },
};
const MIN_CHARS = 3;
const NoRowsOverlay = () => (
  <Typography
    variant='body2'
    sx={{
      padding: 2,
      textAlign: 'center',
      color: '#101828',
      backgroundColor: 'transparent',

      background: 'rgb(255, 251, 241)',
    }}
  >
    No rows
  </Typography>
);

const defaultCellFormatters = (type: any, value: any) => {
  if (!value) return '';
  if (type === 'number') return formatNumber(value);
  if (type === 'date') return formatDate(value);
  if (type === 'dateTime') return formatDateTime(value);
  if (type === 'appDateTime') return formatAppointmentDateTime(value);
  if (type === 'phone') return formatPhoneNumber(value);
  if (type === 'string') return safeString(value);
  return value?.toString();
};

// Styled DataGrid component
const BaseStyledGrid = styled(DataGrid)<DataGridProps>(({ theme }) => ({
  border: 'none',
  backgroundColor: COLORS.LightYellow,
  '.MuiDataGrid-cellContent': {
    fontSize: 14,
    fontWeight: 400,
    color: '#101828',
    textFont: 'Graphik-Regular,sans-serif',
  },

  // Style for ascending sort icon
  '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer .MuiDataGrid-sortIcon': {
    color: '#0606fada', // Yellow
    backgroundColor: '#c2c2c533',
    borderRadius: '2%',
  },
  // Style for descending sort icon
  '& .MuiDataGrid-columnHeader--sorted .MuiDataGrid-iconButtonContainer .MuiDataGrid-sortIcon[data-sort="desc"]': {
    color: '#0606fada', // Dark blue
    backgroundColor: '#f5f5f733',
    borderRadius: '2%',
  },
  // Smooth transition
  '& .MuiDataGrid-sortIcon': {
    transition: 'color 0.2s ease, background-color 0.2s ease',
  },

  // Style for filter icon when filter is applied
  '& .MuiDataGrid-columnHeader--filtered .MuiDataGrid-iconButtonContainer .MuiDataGrid-filterIcon': {
    color: '#0606fada', // Dark blue
    backgroundColor: '#f5f5f733', // Optional: light blue background
    borderRadius: '2%',
  },
  // Smooth transition
  '& .MuiDataGrid-filterIcon': {
    transition: 'color 0.2s ease, background-color 0.2s ease',
  },

  '.MuiDataGrid-toolbarContainer': { background: 'rgb(255, 251, 241)' },
  '.MuiDataGrid-footerContainer': { background: 'rgb(255, 251, 241)' },
  '.MuiDataGrid-columnHeaders': {
    marginBottom: 16,
    boxShadow: 'rgba(0, 0, 0, 0.16) 0px 2px 2px 0px',
    background: COLORS.White,
  },
  '.MuiDataGrid-columnHeadersInner': { background: COLORS.White },
  '.MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },

  '.MuiDataGrid-iconButtonContainer.Mui-active': {
    color: '#1976d2',
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: COLORS.White,
    '&:hover, &.Mui-hovered': { backgroundColor: COLORS.LightBlue },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: COLORS.LightGray,
    '&:hover, &.Mui-hovered': { backgroundColor: COLORS.LightBlue },
  },
  '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
    outline: 'none !important',
  },
  '&.MuiDataGrid-root .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 400,
    fontSize: '16px',
    color: '#000000',
    lineHeight: '24px',
  },

  '& .MuiDataGrid-panel': {
    width: 'fit-content',     // 🔑 grow to content
    maxWidth: '100vw',
  },

  '&.MuiDataGrid-panelContent': {
    padding: '11px',
    overflowX: 'hidden',     // 🔑 kill horizontal scroll
  },
  '&.MuiDataGrid-panelHeader': {
    paddingBottom: 4,
  },
  /* 🔥 FIX: DataGrid filter panel layout */
  '&.MuiDataGrid-filterForm': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
    alignItems: 'center',
  },

  /* Remove default margins that cause wrapping */
  '& .MuiDataGrid-filterForm > *': {
    margin: '0 !important',
    minWidth: 0,
  },

  /* Ensure inputs don’t overflow */
  '& .MuiDataGrid-filterForm .MuiFormControl-root': {
    gridColumn: 'span 2',
  },
}));

// Define interfaces for type safety
interface CustomAction {
  label: string;
  onClick: () => void;
}

interface ToolbarOptions {
  customActions?: CustomAction[];
}

interface CustomDataGridProps {
  rows: GridRowsProp;
  columns: GridColDef[];
  toolbarOptions?: ToolbarOptions;
  className?: string;
  dashBordKey?: string;
  onRowSelect?: (params: any) => void;
  userId?: number;
  defaultFilter?: GridFilterModel;
  onColumnOrderChange?: (params: GridColumnOrderChangeParams, newOrder: string[]) => void;
  setting?: {
    disableSearch?: boolean;
    disableDensity?: boolean;
    disableFilter?: boolean;
    disableManageColumn?: boolean;
    disableExport?: boolean;
  };
  [key: string]: any; // Allow additional DataGrid props
}

// Custom Toolbar Component with Search
const CustomToolbar: FC<
  ToolbarOptions & { onSearch: (searchText: string) => void; onClear: () => void; searchText: string; setting: any }
> = ({ customActions = [], onSearch, onClear, searchText, setting }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onSearch(value);
  };
  const handleCsvExportClick = () => {
    console.log('CSV export triggered at:', new Date().toISOString());
  };
  return (
    <GridToolbarContainer className='MuiDataGrid-toolbarContainer flex gap-2 items-center'>
      {!setting?.disableManageColumn && <GridToolbarColumnsButton sx={btnSx} />}
      {!setting?.disableFilter && <GridToolbarFilterButton sx={btnSx} />}
      {setting?.disableDensity && <GridToolbarDensitySelector sx={btnSx} />}
      {!setting?.disableExport && (
        <GridToolbarExport
          sx={btnSx}
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{
            fileName: 'exported-data',
            utf8WithBom: true,
          }}
          onClick={handleCsvExportClick}
        />
      )}
      {!setting?.disableSearch && (
        <TextField
          variant='outlined'
          size='small'
          value={searchText}
          onChange={handleChange}
          sx={{ width: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <GridSearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position='end'>
                <IconButton onClick={onClear}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
      {customActions.map((action, index) => (
        <Button
          key={index}
          onClick={action.onClick}
          variant='contained'
          size='small'
          sx={{ px: 1.5, py: 0.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#115293' } }}
        >
          {action.label}
        </Button>
      ))}
    </GridToolbarContainer>
  );
};

// Custom Data Grid Component
const CustomDataGrid: FC<CustomDataGridProps> = ({
  rows,
  columns,
  toolbarOptions = {},
  className = '',
  onColumnOrderChange,
  onRowSelect,
  dashBordKey = '',
  userId = -1,
  defaultFilter = { items: [] },
  setting = {
    disableSearch: false,
    disableDensity: false,
    disableFilter: false,
    disableManageColumn: false,
    disableExport: false,
  },
  ...props
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [searchText, setSearchText] = useState('');
  const [searchFilteredRows, setSearchFilteredRows] = useState<GridRowsProp>(rows);
  const [visibleColumns, setVisibleColumns] = useState<GridColDef[]>(columns);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [visibleCount, setVisibleCount] = useState(rows.length);
  const apiRef = useGridApiRef();

  // Handle search using column-specific filter operators
  const handleSearch = (value: string) => {
    setSearchText(value);
    if (value?.length < MIN_CHARS) {
      setSearchFilteredRows(rows);
      setVisibleCount(rows.length);
      return;
    }
    const lowerSearchText = value?.toLowerCase()?.trim();
    const filteredRows = rows.filter((row) =>
      visibleColumns.some((col) => {
        //  if (col.filterable === false) return false;
        const rawValue = row[col.field];
        if (rawValue === null || rawValue === undefined) return false;
        const formattedValue = defaultCellFormatters(col.type, rawValue);
        if (col.type === 'number') {
          return formattedValue === lowerSearchText;
        }
        return (
          formattedValue?.toLowerCase()?.includes(lowerSearchText) || formattedValue?.toLowerCase() === lowerSearchText
        );
      })
    );
    setSearchFilteredRows(filteredRows);
    setVisibleCount(filteredRows.length);
  };

  const handleClear = () => {
    setSearchText('');
    setSearchFilteredRows(rows);
    setVisibleCount(rows.length);
  };

  // Backend save wrapper
  const saveGridState = useCallback(
    async (partialState: any) => {
      const data = {
        settings: partialState,
        gridName: dashBordKey,
        user: userId,
      };
      await dispatch(updateGridSettings(data))
        .unwrap()
        .then((response: any) => {
          if (response?.status === 200) {
            console.log('setting seaved successfully ', dashBordKey, ' ', userId);
          }
        });
    },
    [userId, dashBordKey]
  );

  const loadGridState = useCallback(async () => {
    const data = { gridName: dashBordKey };
    await dispatch(getGridSettings(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const savedSettings = response?.data && response?.data?.settings ? response.data.settings : {};
          //save backend filter model to state
          if (savedSettings?.filterModel?.items?.length > 0) {
            setFilterModel(savedSettings?.filterModel);
          } else {
            //save default filter model to state
            setFilterModel(defaultFilter);
          }

          //save backend column visibility model to state
          setColumnVisibilityModel(savedSettings.columnVisibilityModel || {});
          //apply column visibility model to state
          setVisibleColumns(columns.filter((col) => savedSettings?.columnVisibilityModel?.[col.field] !== false));
          setLoading(false);
        }
      });
  }, [ dashBordKey, dispatch]);

  // Load from backend on mount
  useEffect(() => {
    setLoading(true);
    loadGridState();
  }, [loadGridState]);

  // Load column visibility and order on mount
  useEffect(() => {
    setSearchText('');
    setSearchFilteredRows(rows);
    setVisibleCount(rows.length);
  }, [rows]);
  return (
    <div className={`custom-data-grid w-full h-[600px] ${className}`}>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={70} color={'info'} thickness={2} />
        </Box>
      )}
      <Box
        sx={{
          padding: '8px',
          fontWeight: 'bold',
          background: '#FFFBF1',
        }}
      >
        Showing {visibleCount} / {rows.length} records
      </Box>
      <BaseStyledGrid
        rows={searchFilteredRows}
        apiRef={apiRef}
        columns={columns.map((col) => ({
          ...col,
          disableReorder: col.disableReorder !== true, // Enable reordering unless explicitly disabled
          valueFormatter: col.valueFormatter || (({ value, field }) => defaultCellFormatters(field, value)),
          // eslint-disable-next-line max-len
          filterable: col.filterable !== false && columnVisibilityModel[col.field] !== false, // Disable filtering for hidden columns
        }))}
        getRowId={(row) =>
          dashBordKey === 'ConversationList_0' || dashBordKey === 'ConversationList_1'
            ? row.groupId
            : row.patientId || row.id
        }
        pagination
        filterModel={filterModel}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model) => {
          const newVisibility = model;
          setColumnVisibilityModel(newVisibility);
          const updatedVisibleColumns = columns.filter((col) => newVisibility[col.field] !== false);
          setVisibleColumns(updatedVisibleColumns);
          // Save BOTH states
          saveGridState({
            columnVisibilityModel: newVisibility,
            filterModel: filterModel,
          });
          handleSearch(searchText); // Reapply search with updated visible columns
        }}
        // Filter change
        onFilterModelChange={(model) => {
          setFilterModel(model);

          // Save BOTH states
          saveGridState({
            columnVisibilityModel: columnVisibilityModel,
            filterModel: model,
          });
        }}
        onStateChange={() => {
          // this fires whenever filtering, sorting, pagination etc. changes
          const visibleRows = Array.from(apiRef.current.getSortedRowIds()).filter(
            (id) => apiRef.current.state.filter.filteredRowsLookup[id] !== false
          );
          setVisibleCount(visibleRows.length);
        }}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
          columns: {
            columnVisibilityModel: columnVisibilityModel,
          },
        }}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            ...toolbarOptions,
            onSearch: handleSearch,
            onClear: handleClear,
            searchText,
            setting,
          },
        }}
        sortingMode='client'
        filterMode='client'
        checkboxSelection
        disableColumnMenu={setting.disableManageColumn}
        disableColumnSelector={false}
        getRowClassName={(params: any) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        rowSpacingType='margin'
        style={{ cursor: 'pointer' }}
        autoHeight
        onRowClick={onRowSelect}
        {...props}
      />
    </div>
  );
};

export default CustomDataGrid;
export type { CustomAction, CustomDataGridProps, ToolbarOptions };
